/**
 * Utility per la validazione e ottimizzazione dei title SEO
 * Garantisce che tutti i title rispettino le best practices SEO (30-70 caratteri)
 * e previene duplicazioni di title e meta description
 */
import { debugLog, debugWarn, debugError } from '@/utils/debugLogger';
interface TitleValidationResult {
  isValid: boolean;
  length: number;
  optimizedTitle: string;
  issues: string[];
}

interface SEODuplicationCheck {
  isDuplicate: boolean;
  conflictingPages: string[];
  suggestedAlternative?: string;
}

// Registry globale per tracciare title e meta description utilizzati
class SEORegistry {
  private static instance: SEORegistry;
  private titleRegistry = new Map<string, string[]>(); // title -> array di pagine che lo usano
  private descriptionRegistry = new Map<string, string[]>(); // description -> array di pagine che lo usano

  static getInstance(): SEORegistry {
    if (!SEORegistry.instance) {
      SEORegistry.instance = new SEORegistry();
    }
    return SEORegistry.instance;
  }

  registerTitle(title: string, pagePath: string): SEODuplicationCheck {
    const normalizedTitle = title.toLowerCase().trim();
    const existingPages = this.titleRegistry.get(normalizedTitle) || [];
    
    if (existingPages.length > 0 && !existingPages.includes(pagePath)) {
      return {
        isDuplicate: true,
        conflictingPages: existingPages,
        suggestedAlternative: this.generateAlternativeTitle(title, pagePath)
      };
    }

    // Registra o aggiorna
    if (!existingPages.includes(pagePath)) {
      existingPages.push(pagePath);
      this.titleRegistry.set(normalizedTitle, existingPages);
    }

    return { isDuplicate: false, conflictingPages: [] };
  }

  registerDescription(description: string, pagePath: string): SEODuplicationCheck {
    const normalizedDescription = description.toLowerCase().trim();
    const existingPages = this.descriptionRegistry.get(normalizedDescription) || [];
    
    if (existingPages.length > 0 && !existingPages.includes(pagePath)) {
      return {
        isDuplicate: true,
        conflictingPages: existingPages,
        suggestedAlternative: this.generateAlternativeDescription(description, pagePath)
      };
    }

    // Registra o aggiorna
    if (!existingPages.includes(pagePath)) {
      existingPages.push(pagePath);
      this.descriptionRegistry.set(normalizedDescription, existingPages);
    }

    return { isDuplicate: false, conflictingPages: [] };
  }

  private generateAlternativeTitle(originalTitle: string, pagePath: string): string {
    // Genera un title alternativo basato sul path della pagina
    const pathSegments = pagePath.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];
    
    if (lastSegment && lastSegment !== 'index') {
      const capitalizedSegment = lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
      return `${originalTitle} - ${capitalizedSegment}`;
    }
    
    return `${originalTitle} - Pagina Speciale`;
  }

  private generateAlternativeDescription(originalDescription: string, pagePath: string): string {
    const pathSegments = pagePath.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];
    
    if (lastSegment && lastSegment !== 'index') {
      return `${originalDescription} Scopri di più nella sezione ${lastSegment}.`;
    }
    
    return `${originalDescription} Informazioni dettagliate disponibili.`;
  }

  // Metodo per ottenere statistiche sui duplicati
  getDuplicationStats(): { titles: number; descriptions: number; totalConflicts: number } {
    let titleConflicts = 0;
    let descriptionConflicts = 0;

    this.titleRegistry.forEach(pages => {
      if (pages.length > 1) titleConflicts++;
    });

    this.descriptionRegistry.forEach(pages => {
      if (pages.length > 1) descriptionConflicts++;
    });

    return {
      titles: this.titleRegistry.size,
      descriptions: this.descriptionRegistry.size,
      totalConflicts: titleConflicts + descriptionConflicts
    };
  }

  // Metodo per resettare il registry (utile per i test)
  reset(): void {
    this.titleRegistry.clear();
    this.descriptionRegistry.clear();
  }
}

/**
 * Valida e ottimizza un title per SEO
 * @param title - Il title da validare
 * @param fallbackSuffix - Suffisso di fallback da aggiungere se il title è troppo corto
 * @returns Risultato della validazione con title ottimizzato
 */
export function validateAndOptimizeTitle(
  title: string,
  fallbackSuffix: string = " - Gildy"
): TitleValidationResult {
  const issues: string[] = [];
  let optimizedTitle = title.trim();
  
  // Rimuovi spazi multipli e caratteri non necessari
  optimizedTitle = optimizedTitle.replace(/\s+/g, ' ');
  
  const length = optimizedTitle.length;
  
  // Verifica lunghezza minima (30 caratteri)
  if (length < 30) {
    issues.push(`Title troppo corto (${length} caratteri, minimo 30)`);
    
    // Se il title non contiene già "Gildy", aggiungi il suffisso
    if (!optimizedTitle.toLowerCase().includes('gildy')) {
      optimizedTitle += fallbackSuffix;
    } else {
      // Se contiene già Gildy, aggiungi contenuto descrittivo
      if (optimizedTitle.toLowerCase().includes('accedi')) {
        optimizedTitle = "Accedi al tuo Account Gildy - Oro e Argento";
      } else if (optimizedTitle.toLowerCase().includes('registrati')) {
        optimizedTitle = "Registrati su Gildy - Prezzi Oro e Argento";
      } else if (optimizedTitle.toLowerCase().includes('reset') || optimizedTitle.toLowerCase().includes('password')) {
        optimizedTitle = "Recupera Password Account Gildy - Accesso Sicuro";
      } else if (optimizedTitle.toLowerCase().includes('profilo')) {
        optimizedTitle = "Il Tuo Profilo Gildy - Gestisci Account e Preferenze";
      } else {
        // Fallback generico
        optimizedTitle += " - Prezzi Oro e Argento";
      }
    }
  }
  
  // Verifica lunghezza massima (70 caratteri)
  if (optimizedTitle.length > 70) {
    issues.push(`Title troppo lungo (${optimizedTitle.length} caratteri, massimo 70)`);
    
    // Tronca intelligentemente mantenendo le parole complete
    const words = optimizedTitle.split(' ');
    let truncated = '';
    
    for (const word of words) {
      const testLength = truncated ? truncated.length + 1 + word.length : word.length;
      if (testLength <= 67) { // Lascia spazio per "..."
        truncated += truncated ? ' ' + word : word;
      } else {
        break;
      }
    }
    
    // Assicurati che contenga sempre "Gildy" se possibile
    if (!truncated.toLowerCase().includes('gildy') && truncated.length <= 60) {
      truncated += " - Gildy";
    }
    
    optimizedTitle = truncated;
  }
  
  const finalLength = optimizedTitle.length;
  const isValid = finalLength >= 30 && finalLength <= 70;
  
  return {
    isValid,
    length: finalLength,
    optimizedTitle,
    issues
  };
}

/**
 * Title ottimizzati predefiniti per le pagine principali
 */
export const OPTIMIZED_TITLES = {
  // Pagine di autenticazione
  LOGIN: "Accedi al tuo Account Gildy - Oro e Argento",
  REGISTER: "Registrati su Gildy - Prezzi Oro e Argento",
  RESET_PASSWORD: "Recupera Password Account Gildy - Accesso Sicuro",
  NEW_PASSWORD: "Imposta Nuova Password Gildy - Sicurezza Account",
  
  // Pagine principali
  HOME: "Gildy - Prezzi Oro e Argento in Tempo Reale | Quotazioni Live",
  PROFILE: "Il Tuo Profilo Gildy - Gestisci Account e Preferenze",
  FAQ: "Domande Frequenti su Gildy - Guida Completa Oro e Argento",
  CHARTS: "Grafici Oro e Argento in Tempo Reale - Gildy",
  ALERTS: "Avvisi di Prezzo per Oro e Argento - Gildy",
  GILDY_ASSISTANT: "Assistente AI - Consulente Virtuale per Oro e Argento",
  PREDICTIONS: "Previsioni AI Metalli Preziosi | Gildy",
  BUSINESS_REGISTRATION: "Registra la tua Attività su Gildy - Compro Oro",
  
  // Nuove pagine richieste
  REGISTRA_ATTIVITA: "Registra la tua Attività Compro Oro - Gildy",
  GILDY_PAGE: "Assistente AI Oro e Argento | Valutazioni - Gildy",
  
  // Pagine con titoli duplicati - ORA UNICI
  VALUTAZIONI: "Valutazione Oro e Argento Online - Calcola il Valore | Gildy",
  BLOCCO_PREZZO: "Blocca Prezzo Oro - Proteggi la Tua Quotazione | Gildy",
  APP_VENDERE_ORO: "App Vendere Oro - Guida Completa alla Vendita | Gildy",
  VALUTAZIONE_GIOIELLI: "Valutazione Gioielli Online - Stima Gratuita | Gildy",
  DIFFERENZA_ORO: "Differenza Oro da Investimento vs Oro Usato | Gildy",
  GUIDA_VENDITA: "Guida Vendita Oro e Argento - Come Vendere | Gildy",
  PREVIEW_COMPRO_ORO: "Anteprima Compro Oro - Preview Dealer | Gildy",
  
  // Pagine di errore
  NOT_FOUND: "Pagina Non Trovata - Gildy",
  
  // Template per pagine dinamiche
  BLOG_POST: (title: string) => validateAndOptimizeTitle(title).optimizedTitle,
  DEALER_DETAIL: (dealerName: string, city: string) => 
    validateAndOptimizeTitle(`${dealerName} - Compro Oro a ${city} - Gildy`).optimizedTitle,
  DEALERS_BY_CITY: (city: string) => 
    validateAndOptimizeTitle(`Compro Oro ${city} - Trova la Quotazione Migliore | Gildy`).optimizedTitle,
} as const;

/**
 * Valida un title e mostra warning in development se non conforme
 * @param title - Title da validare
 * @param pageName - Nome della pagina per il logging
 */
export function validateTitleForDevelopment(title: string, pageName: string = 'Unknown'): void {
  if (process.env.NODE_ENV === 'development') {
    const result = validateAndOptimizeTitle(title);
    
    if (!result.isValid) {
      debugWarn('ssr', `SEO Warning - ${pageName}`, {
        currentTitle: title,
        length: result.length,
        issues: result.issues,
        suggestedTitle: result.optimizedTitle
      }, 'seoUtils');
    }
  }
}

/**
 * Valida title e description per duplicati e conformità SEO
 * @param title - Title da validare
 * @param description - Meta description da validare
 * @param pagePath - Path della pagina per il tracking
 * @returns Risultato della validazione completa
 */
export function validateSEOContent(
  title: string,
  description: string,
  pagePath: string
): {
  title: TitleValidationResult & SEODuplicationCheck;
  description: SEODuplicationCheck & { length: number; isValid: boolean };
  isValid: boolean;
} {
  const registry = SEORegistry.getInstance();
  
  // Valida il title
  const titleValidation = validateAndOptimizeTitle(title);
  const titleDuplication = registry.registerTitle(titleValidation.optimizedTitle, pagePath);
  
  // Valida la description
  const descriptionLength = description.length;
  const descriptionValid = descriptionLength >= 120 && descriptionLength <= 160;
  const descriptionDuplication = registry.registerDescription(description, pagePath);
  
  const titleResult = {
    ...titleValidation,
    ...titleDuplication
  };
  
  const descriptionResult = {
    ...descriptionDuplication,
    length: descriptionLength,
    isValid: descriptionValid
  };
  
  // Log warnings in development
  if (process.env.NODE_ENV === 'development') {
    if (titleResult.isDuplicate) {
      debugWarn('ssr', `SEO Duplicate Title - ${pagePath}`, {
        title: titleResult.optimizedTitle,
        conflictingPages: titleResult.conflictingPages,
        suggestedAlternative: titleResult.suggestedAlternative
      }, 'seoUtils');
    }
    
    if (descriptionResult.isDuplicate) {
      debugWarn('ssr', `SEO Duplicate Description - ${pagePath}`, {
        description: description,
        conflictingPages: descriptionResult.conflictingPages,
        suggestedAlternative: descriptionResult.suggestedAlternative
      }, 'seoUtils');
    }
    
    if (!descriptionResult.isValid) {
      debugWarn('ssr', `SEO Description Warning - ${pagePath}`, {
        description: description,
        length: descriptionLength,
        issue: descriptionLength < 120 ? 'Troppo corta (minimo 120 caratteri)' : 'Troppo lunga (massimo 160 caratteri)'
      }, 'seoUtils');
    }
  }
  
  return {
    title: titleResult,
    description: descriptionResult,
    isValid: titleResult.isValid && descriptionResult.isValid && !titleResult.isDuplicate && !descriptionResult.isDuplicate
  };
}

/**
 * Genera title e description unici per pagine dinamiche
 * @param baseTitle - Title base
 * @param baseDescription - Description base
 * @param dynamicData - Dati dinamici per personalizzare il contenuto
 * @param pagePath - Path della pagina
 * @returns Title e description ottimizzati e unici
 */
export function generateUniqueSEOContent(
  baseTitle: string,
  baseDescription: string,
  dynamicData: Record<string, string>,
  pagePath: string
): {
  title: string;
  description: string;
  isUnique: boolean;
} {
  const registry = SEORegistry.getInstance();
  
  // Sostituisci i placeholder nei template
  let title = baseTitle;
  let description = baseDescription;
  
  Object.entries(dynamicData).forEach(([key, value]) => {
    const placeholder = `{${key}}`;
    title = title.replace(new RegExp(placeholder, 'g'), value);
    description = description.replace(new RegExp(placeholder, 'g'), value);
  });
  
  // Ottimizza il title
  const titleValidation = validateAndOptimizeTitle(title);
  let finalTitle = titleValidation.optimizedTitle;
  
  // Verifica unicità del title
  const titleDuplication = registry.registerTitle(finalTitle, pagePath);
  if (titleDuplication.isDuplicate && titleDuplication.suggestedAlternative) {
    finalTitle = titleDuplication.suggestedAlternative;
  }
  
  // Ottimizza la description se necessario
  let finalDescription = description;
  if (description.length > 160) {
    finalDescription = description.substring(0, 157) + '...';
  } else if (description.length < 120) {
    // Aggiungi contenuto descrittivo se troppo corta
    const pathSegments = pagePath.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];
    if (lastSegment) {
      finalDescription += ` Scopri tutto su ${lastSegment} con Gildy.`;
    }
  }
  
  // Verifica unicità della description
  const descriptionDuplication = registry.registerDescription(finalDescription, pagePath);
  if (descriptionDuplication.isDuplicate && descriptionDuplication.suggestedAlternative) {
    finalDescription = descriptionDuplication.suggestedAlternative;
  }
  
  return {
    title: finalTitle,
    description: finalDescription,
    isUnique: !titleDuplication.isDuplicate && !descriptionDuplication.isDuplicate
  };
}

/**
 * Ottiene statistiche sui duplicati SEO
 * @returns Statistiche complete sui duplicati
 */
export function getSEODuplicationStats(): {
  titles: number;
  descriptions: number;
  totalConflicts: number;
  hasConflicts: boolean;
} {
  const registry = SEORegistry.getInstance();
  const stats = registry.getDuplicationStats();
  
  return {
    ...stats,
    hasConflicts: stats.totalConflicts > 0
  };
}

/**
 * Resetta il registry SEO (utile per i test)
 */
export function resetSEORegistry(): void {
  const registry = SEORegistry.getInstance();
  registry.reset();
}

/**
 * Template predefiniti per meta description
 */
export const OPTIMIZED_DESCRIPTIONS = {
  home: "Confronta prezzi oro e argento in tempo reale. Trova i migliori compro oro o argento vicino a te con quotazioni aggiornate e valutazioni.",
  gildy: "Scopri l'Assistente AI: Il ChatBot specializzato nella compra vendita di metalli preziosi. Chiedi informazioni sui migliori compro oro e argento.",
  dealers: "Trova compro oro e argento verificati nella tua zona. Confronta quotazioni, leggi recensioni e scegli il miglior compro oro per vendere i tuoi preziosi.",
  faq: "Domande frequenti su vendita oro e argento. Tutto quello che devi sapere su quotazioni, valutazioni e come scegliere il miglior compro oro.",
  FAQ: "Risposte alle domande più comuni su oro e argento. Guida completa per vendere metalli preziosi e scegliere il compro oro giusto per te.",
  registrazione: "Registra la tua attività di compro oro su Gildy. Aumenta la visibilità, attrai nuovi clienti e gestisci le quotazioni dei metalli preziosi online.",
  REGISTRA_ATTIVITA: "Iscriviti come compro oro su Gildy per far crescere la tua attività. Gestisci quotazioni di oro e argento, raggiungi nuovi clienti e aumenta le vendite.",
  GILDY_PAGE: "Conosci l'Assistente AI per metalli preziosi. Ottieni consigli personalizzati sulla compravendita di oro e argento con tecnologia avanzata.",
  blog: "News, analisi e consigli su oro e argento. Resta aggiornato sui mercati dei metalli preziosi, quotazioni e strategie di investimento con il blog di Gildy.",
  charts: "Grafici e analisi dei prezzi di oro e argento in tempo reale. Monitora l'andamento dei metalli preziosi con strumenti professionali e dati storici.",
  alerts: "Imposta alert personalizzati sui prezzi di oro e argento. Ricevi notifiche quando i metalli preziosi raggiungono i tuoi target di prezzo.",
  profile: "Gestisci il tuo profilo Gildy. Configura preferenze, alert prezzi e impostazioni per un'esperienza personalizzata nel mondo dei metalli preziosi.",
  // Nuove descrizioni per pagine con duplicati
  VALUTAZIONI: "Calcola il valore del tuo oro e argento online. Inserisci peso e purezza per ottenere una valutazione immediata basata sui prezzi di mercato.",
  BLOCCO_PREZZO: "Blocca il prezzo dell'oro per 24 ore e proteggi la tua quotazione dalle fluttuazioni di mercato. Servizio gratuito Gildy.",
  APP_VENDERE_ORO: "Scopri la guida completa per vendere oro con le migliori app. Confronta servizi, prezzi e trova la soluzione ideale per i tuoi preziosi.",
  VALUTAZIONE_GIOIELLI: "Ottieni una stima gratuita dei tuoi gioielli online. Valutazione professionale di oro, argento e pietre preziose con Gildy.",
  DIFFERENZA_ORO: "Scopri le differenze tra oro da investimento e oro usato. Guida completa per scegliere la forma di oro più adatta alle tue esigenze.",
  GUIDA_VENDITA: "Tutto quello che devi sapere per vendere oro e argento al miglior prezzo. Consigli, quotazioni e strategie per massimizzare il valore.",
  PREVIEW_COMPRO_ORO: "Anteprima del profilo compro oro. Visualizza come apparirà la tua attività ai clienti su Gildy prima della pubblicazione."
} as const;

/**
 * Template per meta description dinamiche
 */
export const DYNAMIC_DESCRIPTIONS = {
  DEALER_DETAIL: (dealerName: string, city: string) => 
    `${dealerName} a ${city}: prezzi, recensioni e informazioni complete. Confronta le quotazioni per oro e argento con Gildy.`,
  
  DEALERS_BY_CITY: (city: string) => 
    `Compro oro ${city} - Trova i migliori negozi compro oro nella tua città. Confronta prezzi, recensioni verificate e quotazioni oro e argento in tempo reale per vendere al miglior prezzo.`,
  
  BLOG_POST: (title: string) => 
    `${title.substring(0, 100)}... Leggi l'articolo completo su Gildy per approfondimenti su oro, argento e mercati finanziari.`,
} as const;

/**
 * Verifica se un URL è HTTPS e usa il dominio corretto
 * @param url - URL da verificare
 * @returns true se l'URL è conforme
 */
export function validateUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'https:' && urlObj.hostname === 'www.gildy.it';
  } catch {
    return false;
  }
}

/**
 * Normalizza un URL per assicurarsi che usi HTTPS e www.gildy.it
 * @param path - Path relativo o URL completo
 * @returns URL normalizzato
 */
/**
 * Optimizes meta tags for better SEO by updating DOM elements
 */
export function optimizeMetaTags(seoData: {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  /** @deprecated keywords meta tag removed */
  keywords?: string[];
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
}): void {
  if (typeof window === 'undefined') return;

  try {
    // Update document title
    if (seoData.title) {
      document.title = seoData.title;
    }

    // Update meta description
    if (seoData.description) {
      updateOrCreateMetaTag('name', 'description', seoData.description);
    }

    // Update canonical URL
    if (seoData.canonicalUrl) {
      updateOrCreateLinkTag('canonical', seoData.canonicalUrl);
    }

    // Update Open Graph tags
    if (seoData.ogTitle) {
      updateOrCreateMetaTag('property', 'og:title', seoData.ogTitle);
    }
    if (seoData.ogDescription) {
      updateOrCreateMetaTag('property', 'og:description', seoData.ogDescription);
    }
    if (seoData.ogImage) {
      updateOrCreateMetaTag('property', 'og:image', seoData.ogImage);
    }

    // keywords meta tag removed - Google ignores it since 2009

    if (process.env.NODE_ENV === 'development') {
      debugLog('ssr', 'Updated DOM meta tags', seoData, 'optimizeMetaTags');
    }
  } catch (error) {
    debugError('ssr', 'Error updating meta tags', error, 'optimizeMetaTags');
  }
}

/**
 * Helper function to update or create meta tags
 */
function updateOrCreateMetaTag(attribute: string, name: string, content: string): void {
  if (typeof window === 'undefined') return;

  let metaTag = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  
  if (metaTag) {
    metaTag.content = content;
  } else {
    metaTag = document.createElement('meta');
    metaTag.setAttribute(attribute, name);
    metaTag.content = content;
    document.head.appendChild(metaTag);
  }
}

/**
 * Helper function to update or create link tags
 */
function updateOrCreateLinkTag(rel: string, href: string): void {
  if (typeof window === 'undefined') return;

  let linkTag = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  
  if (linkTag) {
    linkTag.href = href;
  } else {
    linkTag = document.createElement('link');
    linkTag.rel = rel;
    linkTag.href = href;
    document.head.appendChild(linkTag);
  }
}

/**
 * Initialize performance optimizations for blog pages
 */
export function initializeBlogPerformanceOptimizations(): void {
  debugLog('ssr', 'Initializing blog performance optimizations', undefined, 'seoUtils');
}

export function normalizeUrl(path: string): string {
  // Se è già un URL completo, verifica e correggi se necessario
  if (path.startsWith('http')) {
    try {
      const url = new URL(path);
      return `https://www.gildy.it${url.pathname}${url.search}${url.hash}`;
    } catch {
      return `https://www.gildy.it${path}`;
    }
  }
  
  // Se è un path relativo, costruisci l'URL completo
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `https://www.gildy.it${cleanPath}`;
}
